import { forwardRef, useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Input } from '@shared/components/ui/input';
import { Button } from '@shared/components/ui/button';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';
import { UsersApiAdapter } from '../../adapters/UsersApiAdapter';
import { useRoles } from '../../hooks/useRoles';
import { useAuthStore } from '@shared/stores/authStore';
import { InvitationsList } from '../invitations/InvitationsList';
import { usePendingInvitations } from '../../hooks/usePendingInvitations';

interface InviteUserModalProps {
  onSuccess?: () => void;
}

/**
 * Modal para invitar un nuevo usuario
 * Permite ingresar email y asignar un rol inicial
 */
export const InviteUserModal = forwardRef<ModalCenterRef, InviteUserModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('usersModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { roles, isLoading: isLoadingRoles } = useRoles();
  const { team } = useAuthStore();

  const [email, setEmail] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [isInviting, setIsInviting] = useState(false);
  const [activeTab, setActiveTab] = useState<'invite' | 'pending'>('invite');

  // 🎯 Activar polling cuando estamos en el tab "pending"
  // Asumimos que si el tab está activo, el modal está abierto
  const shouldPoll = activeTab === 'pending';

  // 🎯 Callback cuando se detecta que una invitación fue aceptada
  const handleInvitationAccepted = useMemo(
    () => (previousCount: number, currentCount: number) => {
      // Invalidar queries de usuarios e invitaciones para refrescar ambos
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['pending-invitations'] });

      // Mostrar notificación al usuario
      toast({
        title: t('modals.invite.notifications.invitationAccepted.title', 'Invitación aceptada'),
        description: t(
          'modals.invite.notifications.invitationAccepted.description',
          'Una invitación ha sido aceptada. La lista de usuarios se ha actualizado.',
        ),
        variant: 'default',
      });

      // Llamar al callback de éxito para actualizar el listado principal
      onSuccess?.();
    },
    [queryClient, toast, t, onSuccess],
  );

  // 🎯 Hook de invitaciones pendientes con polling activo cuando corresponde
  const { invitations } = usePendingInvitations(shouldPoll, handleInvitationAccepted);

  // 🎯 Inicializar el adapter para operaciones de usuario
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const usersAdapter = useMemo(() => new UsersApiAdapter(httpClient), [httpClient]);

  // 🎯 Convertir roles a formato de opciones para el Combobox
  const roleOptions = useMemo(() => {
    return roles.map((role) => ({
      label: role.name,
      value: role.id.toString(),
    }));
  }, [roles]);

  // 🎯 Validar email
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 🎯 Limpiar formulario
  const resetForm = () => {
    setEmail('');
    setSelectedRoleId('');
  };

  // 🎯 Manejar cierre del modal
  const handleClose = () => {
    resetForm();
    (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
  };

  // 🎯 Manejar invitación de usuario
  const handleInvite = async () => {
    // Validar campos requeridos
    if (!email.trim()) {
      toast({
        title: t('modals.invite.errors.emailRequired'),
        description: t('modals.invite.errors.emailRequiredDesc'),
        variant: 'destructive',
      });
      return;
    }

    if (!isValidEmail(email.trim())) {
      toast({
        title: t('modals.invite.errors.invalidEmail'),
        description: t('modals.invite.errors.invalidEmailDesc'),
        variant: 'destructive',
      });
      return;
    }

    if (!selectedRoleId) {
      toast({
        title: t('modals.invite.errors.roleRequired'),
        description: t('modals.invite.errors.roleRequiredDesc'),
        variant: 'destructive',
      });
      return;
    }

    setIsInviting(true);

    try {
      const response = await usersAdapter.inviteUser({
        email: email.trim(),
        role_id: parseInt(selectedRoleId),
      });

      // 🎯 Si la respuesta es exitosa (200 OK), mostrar toast de éxito
      if (response.success) {
        toast({
          title: t('modals.invite.success.title'),
          description: response.message || t('modals.invite.success.description'),
          variant: 'default',
        });
        // 🎯 Limpiar el formulario después de enviar exitosamente
        resetForm();
        // Invalidar query de invitaciones pendientes para refrescar la lista
        queryClient.invalidateQueries({ queryKey: ['pending-invitations'] });
        // Cambiar al tab de invitaciones pendientes después de invitar exitosamente
        // Esto activará automáticamente el polling
        setActiveTab('pending');
      } else {
        // 🎯 Si hay error, mostrar el mensaje de error de la API
        toast({
          title: t('modals.invite.errors.title'),
          description: response.message || t('modals.invite.errors.unexpected'),
          variant: 'destructive',
        });
      }

      // 🎯 No cerrar modal automáticamente, solo hacer refetch
      onSuccess?.();
    } catch (error: any) {
      // 🎯 Extraer mensaje de error como string
      let errorMessage = t('modals.invite.errors.unexpected');

      if (error?.response?.data) {
        const errorData = error.response.data;

        // La API devuelve el error en errorData.error.message
        if (
          errorData.error &&
          typeof errorData.error === 'object' &&
          errorData.error.message &&
          typeof errorData.error.message === 'string'
        ) {
          errorMessage = errorData.error.message;
        }
        // También puede venir directamente en errorData.message
        else if (errorData.message && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
      } else if (error?.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      // 🎯 Mostrar error inesperado
      toast({
        title: t('modals.invite.errors.title'),
        description: errorMessage,
        variant: 'destructive',
      });

      // 🎯 Cerrar modal y hacer refetch incluso en caso de error
      handleClose();
      onSuccess?.();
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <ModalCenter
      ref={ref}
      title={t('modals.invite.title')}
      type="info"
      width="lg"
      classNameBody="max-h-[calc(100vh-120px)] overflow-y-auto"
      data-testid="invite-user-modal"
    >
      <div className="space-y-6">
        {/* Tabs */}
        <div className="border-b border-card-border">
          <nav className="flex gap-4" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('invite')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'invite'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-complementary-text hover:text-foreground hover:border-card-border'
              }`}
              data-testid="tab-invite"
            >
              {t('modals.invite.tabs.invite', 'Invitar Usuario')}
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'pending'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-complementary-text hover:text-foreground hover:border-card-border'
              }`}
              data-testid="tab-pending"
            >
              {t('modals.invite.tabs.pending', 'Invitaciones Pendientes')}
            </button>
          </nav>
        </div>

        {/* Contenido del tab activo */}
        {activeTab === 'invite' ? (
          <>
            {/* Descripción */}
            <p className="text-sm text-complementary-text">{t('modals.invite.description')}</p>

            {/* Campos */}
            <div className="space-y-4">
              {/* Campo de correo electrónico */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-text">{t('modals.invite.fields.email')} *</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('modals.invite.fields.emailPlaceholder')}
                  disabled={isInviting}
                  data-testid="invite-user-email-input"
                />
              </div>
            </div>

            {/* Campo de rol - Una sola columna */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-text">{t('modals.invite.fields.role')} *</label>
              <Combobox
                options={roleOptions}
                value={selectedRoleId}
                setValue={setSelectedRoleId}
                placeholder={
                  isLoadingRoles ? t('modals.invite.fields.roleLoading') : t('modals.invite.fields.rolePlaceholder')
                }
                disabled={isInviting || isLoadingRoles}
                dataTestId="invite-user-role-select"
              />
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isInviting}
                data-testid="invite-user-cancel-button"
                size={'lg'}
              >
                {t('modals.invite.buttons.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleInvite}
                disabled={
                  isInviting || isLoadingRoles || !email.trim() || !isValidEmail(email.trim()) || !selectedRoleId
                }
                data-testid="invite-user-invite-button"
                size={'lg'}
              >
                {isInviting ? t('modals.invite.buttons.inviting') : t('modals.invite.buttons.invite')}
              </Button>
            </div>
          </>
        ) : (
          <InvitationsList externalInvitations={invitations} externalIsLoading={false} externalIsFetching={false} />
        )}
      </div>
    </ModalCenter>
  );
});

InviteUserModal.displayName = 'InviteUserModal';
